import React, { useMemo } from 'react';
import { View } from 'react-native';
import { stylesDesktop, stylesMobile } from './GridItemContentFooterV3.styles';
import { GridItemContentFooterProps } from './GridItemContentFooterV3.types';
import { useGridItemContentFooterLogicV3 } from './GridItemContentFooterV3.logic';
import { observer } from 'mobx-react-lite';
import { useResponsiveScale } from 'src/contexts/ResponsiveScaleContext';
import Animated, { useAnimatedStyle } from 'react-native-reanimated';
import i18n from 'src/app/i18n';
import useResponsiveScreen from 'src/hooks/useResponsiveScreen';
import { PressableHovered } from 'src/editor-v3/components/pressable-hovered/PressableHovered';
import { BadgeQualityAlertV3 } from '@photosi/rn-components/badge-quality-alert-v3/BadgeQualityAlertV3';
import { QuantityItem } from '@photosi/rn-components/quantity-item/QuantityItem';
import { QuaternaryActionButton } from 'src/editor-v3/components/buttons/quaternary-action-button/QuaternaryActionButton';
import { ACTION_BUTTON_MODE } from 'src/editor-v3/components/buttons/quaternary-action-button/QuaternaryActionButton.types';
import { BUTTON_SIZES } from 'src/editor-v3/components/buttons/button/Button.constants';
import { ICON_NAMES } from '@photosi/rn-components/icon/icon.helper';
import { productContentFooterTestID } from 'src/helpers/testIDs.helper';
import { FocusGroups, PRODUCT_FOCUS_ORDER_STEP } from '@photosi/rn-components/focus-provider/FocusProvider.constants';
import { TestIDs } from 'src/constants/testIDs.constants';
import { Icon } from 'src/theme-v2/icon';

export const GridItemContentFooterV3: React.FC<GridItemContentFooterProps> = observer(({ product, index = 0 }) => {
  const { onPressQuality, avoidWidthImage, showQualityAlert } = useGridItemContentFooterLogicV3(product);

  const { isDesktop } = useResponsiveScreen();
  const styles = useMemo(() => (isDesktop ? stylesDesktop : stylesMobile), [isDesktop]);
  const { responsiveScaleSV } = useResponsiveScale();
  const focusOrderBase = index * PRODUCT_FOCUS_ORDER_STEP;
  const maxWidthAnimatedStyle = useAnimatedStyle(() => {
    return {
      width: product.preview.viewBox.width * responsiveScaleSV.value,
    };
  }, [product.preview.viewBox]);

  return (
    <Animated.View style={[styles.container, !avoidWidthImage && maxWidthAnimatedStyle]}>
      <View style={styles.pill}>
        <QuantityItem
          containerStyle={styles.left}
          product={product}
          isWithText={!isDesktop}
          isBackgroundOnDark={true}
          index={index}
        />

        <View style={[styles.right]}>
          {showQualityAlert &&
            (isDesktop ? (
              <PressableHovered
                onPress={() => {
                  onPressQuality();
                }}
                hint={i18n.t('actions:sub-quality')}
                hintStyle={{ bottom: 35, left: '50%', transform: [{ translateX: '-50%' }] }}
                accessibilityLabel={i18n.t('actions:sub-quality')}
                accessibilityRole="button"
                focusGroup={FocusGroups.PRODUCT}
                focusOrder={focusOrderBase + 6}
              >
                <BadgeQualityAlertV3 />
              </PressableHovered>
            ) : (
              <QuaternaryActionButton
                mode={ACTION_BUTTON_MODE.ICON_ONLY}
                size={BUTTON_SIZES.SMALL}
                iconName={ICON_NAMES.Alert}
                onPress={async () => onPressQuality()}
                text={i18n.t('actions:sub-quality')}
                focusId={productContentFooterTestID(product.id, TestIDs.REPLACE)}
                focusOrder={focusOrderBase + 6}
                focusGroup={FocusGroups.PRODUCT}
                iconStyle={{ color: Icon.IconColorAlert }}
              />
            ))}
        </View>
      </View>
    </Animated.View>
  );
});
